import { isValidElementType } from "react-is";
import { breakpoints } from '@headless-shell/shared-styles';

let EnterpriseSearchModule = null;
let mountedInstance = null;
let isCurrentlyMounted = false;

const ENTERPRISE_SEARCH_ROOT_ID = "enterprise-search-root";

function getEnterpriseSearchContainer() {
  return document.querySelector(`#${ENTERPRISE_SEARCH_ROOT_ID}`);
}

function cleanupContainer() {
  const container = getEnterpriseSearchContainer();
  if (container) container.innerHTML = "";
}

async function loadEnterpriseSearchModule() {
  if (EnterpriseSearchModule) return;
  try {
    const module = await import("enterprise_search_ui/EnterpriseSearchApp");
    EnterpriseSearchModule =
      module?.default ?? module?.EnterpriseSearchApp ?? module;
  } catch (error) {
    console.error("❌ Enterprise Search module loading failed:", error);
    throw error;
  }
}

async function loadSearchBarComponent() {
  try {
    const module = await import("enterprise_search_ui/SearchBar");

    const candidates = [
      module?.SearchBar,
      module?.default?.SearchBar,
      module?.default,
      module?.default?.default,
      module,
    ];

    const SearchBarComponent = candidates.find((candidate) =>
      isValidElementType(candidate)
    );

    if (!SearchBarComponent) {
      const exportKeys = module && typeof module === "object" ? Object.keys(module) : [];
      const defaultKeys =
        module?.default && typeof module.default === "object"
          ? Object.keys(module.default)
          : [];

      throw new Error(
        `Invalid SearchBar export from enterprise_search_ui/SearchBar. ` +
          `module keys: [${exportKeys.join(", ")}], default keys: [${defaultKeys.join(", ")}]`
      );
    }

    return SearchBarComponent;
  } catch (error) {
    console.error("❌ SearchBar component loading failed:", error);
    throw error;
  }
}

export async function bootstrap() {
  return Promise.resolve();
}

export async function mount(props) {
  await loadEnterpriseSearchModule();

  const enterpriseSearchContainer = getEnterpriseSearchContainer();
  if (!enterpriseSearchContainer) {
    console.error(
      `❌ Enterprise Search container (#${ENTERPRISE_SEARCH_ROOT_ID}) not found in DOM`
    );
    throw new Error("Enterprise Search container not found");
  }

  cleanupContainer();
  // Treat tablet (≤1024px) as mobile for CMP search; other MFE consumers fall back to 768px.
  window.__CMP_SEARCH_TABLET_BREAKPOINT__ = parseInt(breakpoints.tablet) * 16;
  const { userContext, authenticated, locale, environment, gaClientId, navigate } = props;
  const profile = userContext?.userInfo?.userAppStoreProfiles;
  const userProfileData = profile
    ? {
        ...(profile.businessTypeCodes?.length && { businessTypeCodes: profile.businessTypeCodes }),
        ...(profile.productChannelCodes?.length && { productChannelCode: profile.productChannelCodes }),
      }
    : null;

  let activeSorFilter = null;
  if (!authenticated) {
    try {
      const parsed = JSON.parse(localStorage.getItem("selectedPlatform") || "null");
      activeSorFilter = parsed?.displayName || null;
    } catch {
      // ignore
    }
  }

  const mountOptions = {
    targetElementId: ENTERPRISE_SEARCH_ROOT_ID,
    authenticateUser: authenticated,
    preFillFormOptions: [],
    userInfo: userContext,
    documentType: props.documentType || "MKP",
    userProfileData,
    defaultGaProps: { gclientId: gaClientId || "", adpWebId: userContext?.userInfo?.adpWebID || "" },
    env: environment || "iat",
    language: locale || "en-US",
    navigate: navigate || window.singleSpa?.navigateToUrl,
    navigateToUrl: navigate || window.singleSpa?.navigateToUrl,
    activeSorFilter,
  };

  try {
    if (typeof EnterpriseSearchModule?.mount !== "function") {
      console.error("❌ No mount function found in Enterprise Search module");
      console.log(
        "Available module exports:",
        Object.keys(EnterpriseSearchModule)
      );
      throw new Error("Enterprise Search mount function not found");
    }

    mountedInstance = await EnterpriseSearchModule.mount(
      enterpriseSearchContainer,
      mountOptions
    );
    isCurrentlyMounted = true;
    console.log("✅ EnterpriseSearch mounted successfully", mountOptions);

  } catch (error) {
    console.error("❌ EnterpriseSearch mount error:", error);
    isCurrentlyMounted = false;
    mountedInstance = null;
    cleanupContainer();
    throw error;
  }
}

export async function unmount(props) {
  await loadEnterpriseSearchModule();
  console.log("EnterpriseSearch unmount called");

  if (!isCurrentlyMounted) {
    console.log("EnterpriseSearch not currently mounted, skipping unmount");
    return;
  }

  try {
    if (typeof EnterpriseSearchModule?.unmount === "function") {
      await EnterpriseSearchModule.unmount(props);
      console.log("✅ EnterpriseSearch module unmount completed");
    }
  } catch (error) {
    console.error("⚠️ EnterpriseSearch unmount error:", error);
  } finally {
    cleanupContainer();
    mountedInstance = null;
    isCurrentlyMounted = false;
    delete window.__CMP_SEARCH_TABLET_BREAKPOINT__;
    console.log("✅ EnterpriseSearch cleanup completed");
  }
}

export { loadSearchBarComponent };